import React from 'react';
import { MonthlyForecast } from '../../lib/forecastService';

interface ForecastChartProps {
  forecasts: MonthlyForecast[];
}

const ForecastChart: React.FC<ForecastChartProps> = ({ forecasts }) => {
  if (forecasts.length === 0) {
    return null;
  }

  const hasChurnAdjustment = forecasts.some(f => f.churn_adjusted_total_revenue !== undefined);
  const maxRevenue = Math.max(
    ...forecasts.map(f => hasChurnAdjustment && f.churn_adjusted_total_revenue
      ? Math.max(f.total_revenue, f.churn_adjusted_total_revenue)
      : f.total_revenue
    ),
    1
  );
  const chartHeight = 300;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Revenue Forecast</h3>

      <div className="relative" style={{ height: chartHeight }}>
        <div className="absolute inset-0 flex flex-col justify-between text-sm text-gray-500">
          <div className="flex items-center">
            <span className="w-16 text-right">${(maxRevenue).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            <div className="flex-1 border-t border-gray-200 ml-4" />
          </div>
          <div className="flex items-center">
            <span className="w-16 text-right">${(maxRevenue * 0.75).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            <div className="flex-1 border-t border-gray-200 ml-4" />
          </div>
          <div className="flex items-center">
            <span className="w-16 text-right">${(maxRevenue * 0.5).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            <div className="flex-1 border-t border-gray-200 ml-4" />
          </div>
          <div className="flex items-center">
            <span className="w-16 text-right">${(maxRevenue * 0.25).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            <div className="flex-1 border-t border-gray-200 ml-4" />
          </div>
          <div className="flex items-center">
            <span className="w-16 text-right">$0</span>
            <div className="flex-1 border-t border-gray-200 ml-4" />
          </div>
        </div>

        <div className="absolute bottom-0 left-20 right-0 flex items-end gap-2" style={{ height: chartHeight - 20 }}>
          {forecasts.map((forecast, index) => {
            const baselineTotalHeight = (forecast.total_revenue / maxRevenue) * 100;
            const adjustedTotal = hasChurnAdjustment && forecast.churn_adjusted_total_revenue
              ? forecast.churn_adjusted_total_revenue
              : forecast.total_revenue;
            const adjustedTotalHeight = (adjustedTotal / maxRevenue) * 100;

            const recurringHeight = (forecast.recurring_revenue / forecast.total_revenue) * baselineTotalHeight;
            const proratedHeight = (forecast.prorated_revenue / forecast.total_revenue) * baselineTotalHeight;
            const projectHeight = (forecast.project_revenue / forecast.total_revenue) * baselineTotalHeight;

            const churnAdjustedRecurring = hasChurnAdjustment && forecast.churn_adjusted_recurring_revenue
              ? forecast.churn_adjusted_recurring_revenue
              : forecast.recurring_revenue;

            return (
              <div
                key={forecast.month}
                className="flex-1 group relative"
                style={{ height: '100%' }}
              >
                {hasChurnAdjustment && (
                  <div
                    className="absolute bottom-0 left-0 right-0 bg-gray-200 opacity-30"
                    style={{ height: `${baselineTotalHeight}%` }}
                    title="Baseline (no churn)"
                  />
                )}

                <div className="absolute bottom-0 left-0 right-0 flex flex-col" style={{ height: `${adjustedTotalHeight}%` }}>
                  <div
                    className="bg-blue-500 hover:bg-blue-600 transition-colors"
                    style={{ height: `${(projectHeight / baselineTotalHeight) * (adjustedTotalHeight / baselineTotalHeight) * 100}%` }}
                    title={`Project: $${forecast.project_revenue.toFixed(2)}`}
                  />
                  <div
                    className="bg-orange-400 hover:bg-orange-500 transition-colors"
                    style={{ height: `${(proratedHeight / baselineTotalHeight) * (adjustedTotalHeight / baselineTotalHeight) * 100}%` }}
                    title={`Prorated: $${forecast.prorated_revenue.toFixed(2)}`}
                  />
                  <div
                    className={`${hasChurnAdjustment ? 'bg-green-400' : 'bg-green-500'} hover:bg-green-600 transition-colors`}
                    style={{ height: `${hasChurnAdjustment ? (churnAdjustedRecurring / adjustedTotal) * 100 : (recurringHeight / baselineTotalHeight) * 100}%` }}
                    title={`Recurring: $${churnAdjustedRecurring.toFixed(2)}`}
                  />
                </div>

                <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute -top-32 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg whitespace-nowrap z-10 pointer-events-none">
                  <div className="font-semibold mb-2">{forecast.month}</div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-500 rounded" />
                      <span>Recurring: ${forecast.recurring_revenue.toFixed(2)}</span>
                    </div>
                    {hasChurnAdjustment && forecast.churn_adjusted_recurring_revenue && (
                      <div className="flex items-center gap-2 ml-5">
                        <span className="text-orange-400">After Churn: ${forecast.churn_adjusted_recurring_revenue.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-orange-400 rounded" />
                      <span>Prorated: ${forecast.prorated_revenue.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-500 rounded" />
                      <span>Project: ${forecast.project_revenue.toFixed(2)}</span>
                    </div>
                    <div className="pt-1 mt-1 border-t border-gray-700">
                      <div>Baseline Total: ${forecast.total_revenue.toFixed(2)}</div>
                      {hasChurnAdjustment && forecast.churn_adjusted_total_revenue && (
                        <div className="text-orange-400">Churn Adjusted: ${forecast.churn_adjusted_total_revenue.toFixed(2)}</div>
                      )}
                      <div className="text-green-400 mt-1">Confirmed: ${forecast.confirmed_revenue.toFixed(2)}</div>
                      <div className="text-orange-300">At Risk: ${forecast.at_risk_revenue.toFixed(2)}</div>
                    </div>
                  </div>
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded" />
            <span className="text-gray-700">Recurring Services</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-orange-400 rounded" />
            <span className="text-gray-700">Prorated Payments</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-500 rounded" />
            <span className="text-gray-700">Project Milestones</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForecastChart;